(() => {
  'use strict';
  const body = document.body;
  if (!body || !body.classList.contains('ssp-firstlights')) return;
  if (window.SSPFirstLightsMotion) { window.SSPFirstLightsMotion.init(document); return; }
  const selector = '.ssp-eye-kits-home [data-ssp-firstlights-methow-reveal]';
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const products = new Map();
  let userPaused = body.dataset.seasonMotion === 'paused';
  const updateControls = () => {
    const paused = userPaused || preference.matches;
    body.dataset.seasonMotion = paused ? 'paused' : 'playing';
    document.querySelectorAll('[data-season-motion-toggle]').forEach(button => {
      const labels = button.dataset;
      const label = paused ? labels.seasonMotionPlayLabel : labels.seasonMotionPauseLabel;
      const aria = preference.matches ? labels.seasonMotionReducedAria
        : paused ? labels.seasonMotionPlayAria : labels.seasonMotionPauseAria;
      if (label) button.textContent = label;
      if (aria) button.setAttribute('aria-label', aria);
      button.setAttribute('aria-pressed', String(paused));
      button.disabled = preference.matches;
      button.title = preference.matches ? labels.seasonMotionReducedTitle || '' : '';
    });
  };
  const syncMotion = () => {
    updateControls();
    products.forEach(controller => controller.sync());
  };

  function createProduct(product) {
    product.dataset.motionReady = 'shopify-v7';
    const photo = product.querySelector('.ssp-methow-reveal__photo');
    const visual = product.querySelector('.ssp-methow-reveal__visual');
    const sparkle = product.querySelector('.ssp-methow-reveal__sparkle--third');
    let entryObserver = null;
    let resetObserver = null;
    let generation = 0;
    let imageReady = false;
    let started = false;
    let finished = false;
    let unavailable = false;
    let disposed = false;
    let revealEnteredAt = -Infinity;
    let resetFrame = 0;

    const disconnectEntry = () => {
      generation += 1;
      entryObserver?.disconnect();
      entryObserver = null;
    };
    const settle = () => {
      disconnectEntry();
      product.dataset.motion = 'static';
      if (started) finished = true;
    };
    const syncProduct = () => {
      if (disposed) return;
      if (unavailable || finished || body.dataset.seasonMotion === 'paused' || document.hidden) {
        settle();
        return;
      }
      if (!imageReady) { product.dataset.motion = 'static'; return; }
      if (started || entryObserver) return;
      if (!('IntersectionObserver' in window)) { settle(); return; }
      const currentGeneration = ++generation;
      entryObserver = new IntersectionObserver(entries => {
        if (disposed || currentGeneration !== generation || started || finished || unavailable) return;
        if (body.dataset.seasonMotion === 'paused' || document.hidden) { settle(); return; }
        const entry = entries.filter(item => item.target === product).at(-1);
        if (!entry?.isIntersecting || entry.intersectionRatio < .28) return;
        started = true;
        revealEnteredAt = Number.isFinite(entry.time) ? entry.time : -Infinity;
        product.dataset.motion = 'revealing';
        disconnectEntry();
      }, { threshold: .28, rootMargin: '0px 0px -24px 0px' });
      product.dataset.motion = 'armed';
      entryObserver.observe(product);
    };
    const rearm = () => {
      disconnectEntry();
      started = false;
      finished = false;
      product.dataset.motion = 'static';
      syncProduct();
    };
    const watchForReturn = () => {
      if (resetObserver || !('IntersectionObserver' in window)) return;
      resetObserver = new IntersectionObserver(entries => {
        if (disposed || unavailable || (!started && !finished)) return;
        const entry = entries.filter(item => item.target === product).at(-1);
        if (!entry || entry.isIntersecting || (Number.isFinite(entry.time) && entry.time < revealEnteredAt)) return;
        const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
        const boundary = Number.isFinite(entry.rootBounds?.bottom)
          ? entry.rootBounds.bottom : viewportHeight > 0 ? viewportHeight + 120 : Infinity;
        if (Number.isFinite(entry.boundingClientRect?.top) && entry.boundingClientRect.top >= boundary) rearm();
      }, { threshold: 0, rootMargin: '0px 0px 120px 0px' });
      resetObserver.observe(product);
    };
    const checkReturnPosition = () => {
      if (disposed || unavailable || (!started && !finished) || document.hidden) return;
      const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
      if (viewportHeight > 0 && product.getBoundingClientRect().top >= viewportHeight + 120) rearm();
    };
    const scheduleReturnCheck = () => {
      if (disposed || resetFrame || unavailable || (!started && !finished) || document.hidden) return;
      resetFrame = window.requestAnimationFrame(() => {
        resetFrame = 0;
        checkReturnPosition();
      });
    };
    const onAnimationEnd = event => {
      if (disposed || !started || finished || product.dataset.motion !== 'revealing') return;
      const completed = sparkle
        ? event.target === sparkle && event.animationName === 'sspMethowV7SparkThird'
        : event.target === visual && event.animationName === 'sspMethowV5Arrival';
      if (completed) settle();
    };
    const failedPhoto = () => {
      if (disposed) return;
      unavailable = true;
      settle();
      resetObserver?.disconnect();
      resetObserver = null;
      if (resetFrame) window.cancelAnimationFrame(resetFrame);
      resetFrame = 0;
      product.dataset.imageStatus = 'unavailable';
    };
    const loadedPhoto = () => {
      if (disposed || unavailable) return;
      if (!photo.naturalWidth) { failedPhoto(); return; }
      imageReady = true;
      product.dataset.imageStatus = 'ready';
      watchForReturn();
      syncProduct();
    };
    const controller = {
      sync: syncProduct,
      checkReturnPosition,
      scheduleReturnCheck,
      destroy() {
        if (disposed) return;
        disposed = true;
        disconnectEntry();
        resetObserver?.disconnect();
        resetObserver = null;
        if (resetFrame) window.cancelAnimationFrame(resetFrame);
        resetFrame = 0;
        photo?.removeEventListener('load', loadedPhoto);
        photo?.removeEventListener('error', failedPhoto);
        visual?.removeEventListener('animationend', onAnimationEnd);
        product.dataset.motion = 'static';
        delete product.dataset.motionReady;
      }
    };
    visual?.addEventListener('animationend', onAnimationEnd);
    product.dataset.motion = 'static';
    if (!photo) failedPhoto();
    else {
      photo.addEventListener('error', failedPhoto, { once: true });
      if (photo.complete) loadedPhoto();
      else photo.addEventListener('load', loadedPhoto, { once: true });
    }
    return controller;
  }

  const init = (root = document) => {
    if (!body.classList.contains('ssp-firstlights')) return;
    products.forEach((controller, product) => {
      if (!product.isConnected) { controller.destroy(); products.delete(product); }
    });
    updateControls();
    const candidates = [...(root.querySelectorAll?.(selector) || [])];
    if (root.matches?.(selector)) candidates.unshift(root);
    candidates.forEach(product => {
      if (!products.has(product)) products.set(product, createProduct(product));
    });
    syncMotion();
  };
  window.SSPFirstLightsMotion = { init };
  preference.addEventListener('change', syncMotion);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-season-motion-toggle]');
    if (!button || !body.contains(button) || preference.matches) return;
    event.preventDefault();
    userPaused = !userPaused;
    syncMotion();
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    products.forEach((controller, product) => {
      if (event.target === product || event.target.contains(product)) {
        controller.destroy();
        products.delete(product);
      }
    });
  });
  document.addEventListener('visibilitychange', () => products.forEach(controller => controller.sync()));
  window.addEventListener('scroll', () => products.forEach(controller => controller.scheduleReturnCheck()), { passive: true });
  window.addEventListener('pageshow', () => {
    init(document);
    products.forEach(controller => { controller.checkReturnPosition(); controller.sync(); });
  });
  init(document);
})();
